import { lazy } from 'react';
import type { ComponentType, LazyExoticComponent } from 'react';

const CHUNK_LOAD_RELOAD_KEY = 'portal:chunk-load-reload';

const CHUNK_LOAD_ERROR_PATTERNS = [
    /failed to fetch dynamically imported module/i,
    /error loading dynamically imported module/i,
    /importing a module script failed/i,
    /failed to load module script/i,
    /loading chunk \d+ failed/i,
];

type LazyModule<T extends ComponentType<never>> = { default: T };

const isChunkLoadError = (error: unknown): boolean =>
    error instanceof Error &&
    CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(error.message));

const shouldReloadForChunkLoadError = (): boolean => {
    try {
        if (window.sessionStorage.getItem(CHUNK_LOAD_RELOAD_KEY)) {
            return false;
        }

        window.sessionStorage.setItem(CHUNK_LOAD_RELOAD_KEY, 'true');
        return true;
    } catch {
        return false;
    }
};

const clearChunkLoadReloadAttempt = (): void => {
    try {
        window.sessionStorage.removeItem(CHUNK_LOAD_RELOAD_KEY);
    } catch {
        // Session storage is optional; do not affect a successfully loaded route.
    }
};

/**
 * Reloads once when a deployment removes a lazily imported, hash-named chunk.
 * A successful import clears the guard so a later deployment can recover too.
 */
const lazyWithRetry = <T extends ComponentType<never>>(
    importer: () => Promise<LazyModule<T>>
): LazyExoticComponent<T> =>
    lazy(async () => {
        try {
            const module = await importer();
            clearChunkLoadReloadAttempt();
            return module;
        } catch (error) {
            if (isChunkLoadError(error) && shouldReloadForChunkLoadError()) {
                window.location.reload();

                // Do not render the error boundary while the reload is in progress.
                return new Promise<never>(() => undefined);
            }

            throw error;
        }
    });

export default lazyWithRetry;
